import { z } from 'zod';
import {
  zAchievementId,
  zAvatarFrameId,
  zAvatarPartId,
  zBadgeId,
  zBoxDefId,
  zChapterId,
  zExpansionId,
  zFeatureKey,
  zFlagId,
  zItemId,
  zLetterId,
  zMiniGameId,
  zNpcId,
  zPhotoFrameId,
  zPostcardId,
  zRoomId,
  zSceneId,
  zStickerId,
  zTaskId,
  zTitleId,
  zUnlockId,
} from './ids';

/** What a player receives (content-schemas §2.1). Always applied by RewardService with an idempotency key. */
export const zReward = z.discriminatedUnion('type', [
  z.object({ type: z.literal('coins'), amount: z.number().int().positive() }),
  z.object({ type: z.literal('stars'), amount: z.number().int().positive() }),
  z.object({ type: z.literal('item'), itemId: zItemId, qty: z.number().int().positive().default(1) }),
  z.object({ type: z.literal('title'), titleId: zTitleId }),
  z.object({ type: z.literal('badge'), badgeId: zBadgeId }),
  z.object({ type: z.literal('avatarPart'), partId: zAvatarPartId }),
  z.object({ type: z.literal('avatarFrame'), frameId: zAvatarFrameId }),
  z.object({ type: z.literal('sticker'), stickerId: zStickerId }),
  z.object({ type: z.literal('photoFrame'), frameId: zPhotoFrameId }),
  z.object({ type: z.literal('flag'), flag: zFlagId }),
  z.object({ type: z.literal('npcVisit'), npcId: zNpcId, roomId: zRoomId }),
  z.object({ type: z.literal('unlock'), unlockId: zUnlockId }),
  z.object({ type: z.literal('chapter'), chapterId: zChapterId }),
  z.object({ type: z.literal('postcard'), postcardId: zPostcardId, isRepeat: z.boolean() }),
  z.object({ type: z.literal('letter'), letterId: zLetterId }),
  z.object({ type: z.literal('shopPerk'), perkId: z.enum(['faster_customers', 'regulars_buy_more']) }),
]);
export const zRewards = z.array(zReward).max(8);
export type Reward = z.output<typeof zReward>;
export type RewardInput = z.input<typeof zReward>;

/** Read-only checks against state (content-schemas §2.2). Arrays of conditions are AND. */
export const zCondition = z.discriminatedUnion('type', [
  z.object({ type: z.literal('starsAtLeast'), value: z.number().int().min(0) }),
  z.object({ type: z.literal('taskCompleted'), taskId: zTaskId }),
  z.object({ type: z.literal('unlocked'), unlockId: zUnlockId }),
  z.object({ type: z.literal('friendshipAtLeast'), npcId: zNpcId, level: z.number().int().min(1) }),
  z.object({ type: z.literal('cozinessAtLeast'), roomId: zRoomId, level: z.number().int().min(1) }),
  z.object({ type: z.literal('achievementCompleted'), achievementId: zAchievementId }),
  z.object({ type: z.literal('coinsCost'), amount: z.number().int().positive() }),
  z.object({ type: z.literal('flag'), flag: zFlagId, value: z.boolean() }),
  z.object({ type: z.literal('chapterSeen'), chapterId: zChapterId }),
  z.object({ type: z.literal('boxOpened'), boxDefId: zBoxDefId }),
  z.object({ type: z.literal('miniGameCleared'), miniGameId: zMiniGameId, level: z.number().int().min(1) }),
  /** Tiếng lành Studio level (studio-nano.md §5). */
  z.object({ type: z.literal('studioLevelAtLeast'), level: z.number().int().min(1) }),
]);
export type Condition = z.output<typeof zCondition>;

/** Side effects of an unlock that cannot be derived from references (content-schemas §2.3). */
export const zGrant = z.discriminatedUnion('type', [
  z.object({ type: z.literal('scene'), sceneId: zSceneId }),
  z.object({ type: z.literal('feature'), feature: zFeatureKey }),
  z.object({ type: z.literal('chapter'), chapterId: zChapterId }),
  z.object({ type: z.literal('roomExpansion'), roomId: zRoomId, expansionId: zExpansionId }),
  z.object({ type: z.literal('flag'), flag: zFlagId, value: z.boolean() }),
  z.object({ type: z.literal('reward'), reward: zReward }),
]);
export type Grant = z.output<typeof zGrant>;
